import { MNSproCloudApi } from '..';
import {
  AbsenceDraftResponse,
  AbsenceDraftState,
  AbsenceEntry,
  AbsenceOverview,
  AbsencePutEntry,
  AbsenceReasonResponse,
  AbsenceResponse,
  AbsencesGetOfStudentRequestFilter,
  AvailableFiltersResponse,
  ClassAbsencesResponse,
  CourseAbsencesResponse,
  CourseBasicResponse,
  ExcuseDefinitionResponse,
  GeneralSettingsItem,
  GradeAbsencesResponse,
  GradeBasicResponse,
  PagedOfAbsenceResponse,
  PagedOfArchivedAbsenceDraftResponse,
  Sequence,
  SortableBy,
  StudentAbsenceDetailed,
  StudentAbsenceTimetableResponse,
} from '../api/v2';

const getClassRegisterApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ClassRegisterApiFactory(config);
};

const getAbsencesApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.AbsencesApiFactory(config);
};

export type DeleteAbsenceOutcome =
  | { absenceId: string; success: true }
  | { absenceId: string; success: false; error: unknown };

export const AbsencesService = {
  async getGeneralSettings(
    authToken: string,
    organizationId: string,
  ): Promise<GeneralSettingsItem> {
    const api = getAbsencesApi(authToken);
    const result = await api.getGeneralSettings(organizationId);

    return result.data;
  },

  async getAbsenceReasons(
    authToken: string,
    organizationId: string,
  ): Promise<AbsenceReasonResponse[]> {
    const api = getAbsencesApi(authToken);
    const result = await api.getAllAbsenceReasons(organizationId);

    return result.data;
  },

  async getExcuseDefinitions(
    authToken: string,
    organizationId: string,
  ): Promise<ExcuseDefinitionResponse[]> {
    const api = getAbsencesApi(authToken);
    const result = await api.getAllAbsenceExcuseDefinitions(organizationId);

    return result.data;
  },

  async createAbsenceBulk(
    authToken: string,
    organizationId: string,
    absenceEntry: AbsenceEntry,
    studentIds: string[],
  ) {
    const api = getClassRegisterApi(authToken);
    const result = await api.createBulkAbsences(organizationId, {
      absenceTemplate: absenceEntry,
      studentIds,
    });

    return result.status === 200;
  },

  async getGrades(authToken: string, organizationId: string): Promise<GradeBasicResponse[]> {
    const api = getAbsencesApi(authToken);
    const result = await api.getAllBasicGrades(organizationId);

    return result.data.value;
  },

  async getCourses(authToken: string, organizationId: string): Promise<CourseBasicResponse[]> {
    const api = getAbsencesApi(authToken);
    const result = await api.getAllBasicCourses(organizationId);

    return result.data.value;
  },

  /**
   * Returns all courses the given student is enrolled in, independent of which
   * teacher owns the course. Used by the absences student-detail course filter so
   * that teacher-role users also see the full set of the student's courses.
   */
  async getCoursesOfStudent(
    authToken: string,
    organizationId: string,
    studentId: string,
  ): Promise<CourseBasicResponse[]> {
    const api = getAbsencesApi(authToken);
    const result = await api.getCoursesOfStudent(organizationId, studentId);

    return result.data.value;
  },

  /**
   * Returns all absences for the given group and range.
   */
  // static async getAbsencesByGroupId(
  //   authToken: string,
  //   organizationId: string,
  //   groupId: string,
  //   dayStart: string,
  //   dayEnd: string,
  // ): Promise<AbsenceResponse[]> {
  //   const api = getClassRegisterApi(authToken);
  //   const result = await api.getAllAbsencesMembersOfGroup(
  //     groupId,
  //     organizationId,
  //     undefined,
  //     dayEnd,
  //     dayStart,
  //   );

  //   return result.data;
  // }

  /**
   * Creates an absence entry.
   */
  async createAbsence(
    authToken: string,
    organizationId: string,
    absence: AbsenceEntry,
  ): Promise<AbsenceResponse> {
    const api = getAbsencesApi(authToken);
    const result = await api.createAbsence(organizationId, absence);

    return result.data;
  },

  /**
   * Updates an absence.
   */
  async updateAbsence(
    authToken: string,
    organizationId: string,
    absence: AbsencePutEntry,
  ): Promise<AbsenceResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.updateAbsences(organizationId, absence);

    return result.data;
  },

  /**
   * Updates or creates multiple absences.
   */
  async updateAbsences(
    authToken: string,
    organizationId: string,
    lessonId: string,
    creates: AbsenceEntry[],
    updates: AbsencePutEntry[],
  ): Promise<AbsenceResponse[]> {
    const api = getClassRegisterApi(authToken);
    const result = await api.createOrUpdateAbsences(lessonId, organizationId, {
      creates,
      updates,
    });

    return result.data;
  },

  async getGradeAbsences(
    authToken: string,
    organizationId: string,
    gradeId: string,
    year: number,
    month: number,
  ): Promise<GradeAbsencesResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getGradeAbsences(gradeId, month, organizationId, year);

    return result.data;
  },

  async getCourseAbsences(
    authToken: string,
    organizationId: string,
    courseId: string,
    year: number,
    month: number,
  ): Promise<CourseAbsencesResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getCourseAbsences(courseId, month, organizationId, year);

    return result.data;
  },

  async getAbsenceOverview(
    authToken: string,
    organizationId: string,
    skip: number,
    take: number,
    reasonId: string | undefined,
    sortBy: SortableBy,
    sequence: Sequence,
    abortSignal: AbortSignal | null,
    searchQuery?: string,
  ): Promise<AbsenceOverview> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getAbsenceOverview(
      organizationId,
      skip,
      take,
      reasonId,
      searchQuery,
      sequence,
      sortBy,
      undefined,
      abortSignal === null
        ? undefined
        : {
            signal: abortSignal,
          },
    );

    return result.data;
  },

  async getClassAbsences(
    authToken: string,
    organizationId: string,
    classId: string,
    year: number,
    month: number,
  ): Promise<ClassAbsencesResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getClassAbsences(classId, month, organizationId, year);

    return result.data;
  },

  /**
   * Gets detailed student information (classes, grades, statistics) without absences.
   * Use getStudentAbsencesPaged for fetching the actual absences.
   */
  async getStudentAbsenceDetails(
    authToken: string,
    organizationId: string,
    studentId: string,
    timePeriod?: string,
  ): Promise<StudentAbsenceDetailed> {
    const api = getClassRegisterApi(authToken);
    const result = await api.absencesOfStudentDetails(organizationId, studentId, timePeriod);
    return result.data;
  },

  /**
   * Gets paginated absences for a student with optional filters.
   */
  async getStudentAbsencesPaged(
    authToken: string,
    organizationId: string,
    studentId: string,
    filter?: AbsencesGetOfStudentRequestFilter,
    start?: string,
    end?: string,
    courseId?: string,
    absenceReasonId?: string,
    timePeriod?: string,
    pageSize?: number,
    page?: number,
  ): Promise<PagedOfAbsenceResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.absencesOfStudent(
      organizationId,
      studentId,
      filter,
      start,
      end,
      courseId,
      absenceReasonId,
      timePeriod,
      pageSize,
      page,
    );
    return result.data;
  },

  /**
   * Gets absences for a student within a date range (for conflict detection).
   */
  async getConflictedStudentAbsences(
    authToken: string,
    organizationId: string,
    studentId: string,
    start: string,
    end: string,
    filter?: AbsencesGetOfStudentRequestFilter,
    timePeriod?: string,
    pageSize?: number,
    page?: number,
  ): Promise<PagedOfAbsenceResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.absencesOfStudent(
      organizationId,
      studentId,
      filter,
      start,
      end,
      undefined, // courseId
      undefined, // absenceReasonId
      timePeriod,
      pageSize,
      page,
    );

    return result.data;
  },

  async deleteAbsence(
    authToken: string,
    organizationId: string,
    absenceId: string,
  ): Promise<boolean> {
    const api = getClassRegisterApi(authToken);
    const result = await api.deleteAbsence(absenceId, organizationId);
    return result.status === 200;
  },

  /**
   * Marks the given lessons of a student as attended and returns the attendance
   * entries the backend created (#11411).
   */
  async markLessonsPresent(
    authToken: string,
    organizationId: string,
    studentId: string,
    lessonIds: string[],
    timePeriod?: string,
  ): Promise<AbsenceResponse[]> {
    const api = getClassRegisterApi(authToken);
    const result = await api.markLessonsPresent(
      organizationId,
      studentId,
      { lessonIds },
      timePeriod,
    );

    return result.data;
  },

  async deleteMultipleStudentAbsences(
    authToken: string,
    organizationId: string,
    absenceIds: string[],
  ): Promise<DeleteAbsenceOutcome[]> {
    const settled = await Promise.allSettled(
      absenceIds.map((absenceId) =>
        AbsencesService.deleteAbsence(authToken, organizationId, absenceId),
      ),
    );

    return settled.map((result, index) => {
      const absenceId = absenceIds[index];
      if (result.status === 'fulfilled' && result.value === true) {
        return { absenceId, success: true };
      }
      return {
        absenceId,
        success: false,
        error:
          result.status === 'rejected' ? result.reason : new Error('deleteAbsence returned false'),
      };
    });
  },

  async addAttachment(
    authToken: string,
    organizationId: string,
    timePeriod: string,
    absenceId: string,
    file: File,
  ) {
    const api = getClassRegisterApi(authToken);
    const result = await api.addAttachment(organizationId, timePeriod, absenceId, file);
  },
  /**
   * Retrieves absence attachment contents.
   */
  async getAttachment(
    authToken: string,
    organizationId: string,
    timePeriod: string,
    absenceId: string,
    fileName: string,
  ) {
    const api = getClassRegisterApi(authToken);
    const response = await api.getAttachment(organizationId, absenceId, fileName, timePeriod, {
      responseType: 'blob',
    });

    // The mime type response header is always set to
    // application/octet-stream we have to guess the correct
    // mimetype here.
    let mimeType = 'application/octet-stream';

    if (fileName.endsWith('.png')) {
      mimeType = 'image/png';
    } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
      mimeType = 'image/jpeg';
    }

    const blob = new Blob([response.data], { type: mimeType });

    return blob;
  },

  async deleteAttachment(
    authToken: string,
    organizationId: string,
    timePeriod: string,
    absenceId: string,
    filename: string,
  ) {
    const api = getClassRegisterApi(authToken);
    await api.deleteAttachment(absenceId, organizationId, filename, timePeriod);
  },

  async getAbsenceById(
    authToken: string,
    organizationId: string,
    absenceId: string,
  ): Promise<AbsenceResponse> {
    const api = getClassRegisterApi(authToken);
    const result = await api.getLatestAbsence(absenceId, organizationId);

    return result.data;
  },

  async getStudentAbsencesByDateRange(
    authToken: string,
    organizationId: string,
    studentId: string,
    start: string,
    end: string,
  ): Promise<StudentAbsenceTimetableResponse> {
    const api = getClassRegisterApi(authToken);
    const response = await api.absencesOfStudentTimetableFormatHandler(
      organizationId,
      studentId,
      start,
      end,
    );

    return response.data;
  },

  async getDrafts(
    authToken: string,
    organizationId: string,
    state?: AbsenceDraftState,
  ): Promise<AbsenceDraftResponse[]> {
    const api = getAbsencesApi(authToken);
    const response = await api.getAllAbsenceDrafts(organizationId, undefined, state);

    return response.data;
  },

  async getDraftsCount(
    authToken: string,
    organizationId: string,
    state?: AbsenceDraftState,
  ): Promise<number> {
    const api = getAbsencesApi(authToken);
    const response = await api.getAllAbsenceDraftsCount(organizationId, undefined, state);

    return response.data;
  },

  async updateDraftState(
    authToken: string,
    organizationId: string,
    draftId: string,
    state: AbsenceDraftState,
  ): Promise<AbsenceDraftResponse> {
    const api = getAbsencesApi(authToken);
    const response = await api.updateAbsenceDraftState(organizationId, draftId, state);

    return response.data;
  },

  /**
   * Retrieves available filter options for archived absence drafts
   */
  async getArchivedDraftsAvailableFilters(
    authToken: string,
    organizationId: string,
  ): Promise<AvailableFiltersResponse> {
    const api = getAbsencesApi(authToken);
    const response = await api.getArchivedAbsenceDraftsAvailableFilters(organizationId);

    return response.data;
  },

  /**
   * Retrieves archived absence drafts with pagination and filtering
   */
  async getArchivedDrafts(
    authToken: string,
    organizationId: string,
    page: number,
    pageSize: number,
    studentName?: string,
    className?: string,
    gradeName?: string,
    state?: AbsenceDraftState.accepted | AbsenceDraftState.declined,
  ): Promise<PagedOfArchivedAbsenceDraftResponse> {
    const api = getAbsencesApi(authToken);
    const response = await api.getArchivedAbsenceDrafts(
      organizationId,
      undefined, // timePeriod
      studentName,
      className,
      gradeName,
      state,
      page,
      pageSize,
    );

    return response.data;
  },
};
